/**
 * /completar-registro — ver `MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO`
 *
 * Formulario post-registro: unsafeMetadata. Redirige a `MEMBERSHIP_ROUTE.PENDING_APPROVAL`.
 * Parámetros de URL se propagan (sesión Stripe, MP, plan).
 * Guía pública: `MEMBERSHIP_ACCESS_PORTAL_PATH`.
 */

import { MembershipPortalHint } from "@/components/membership/MembershipPortalHint";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSEO } from "@/hooks/useSEO";
import {
  getRegistrationData,
  isClerkAdmin,
  isProfileComplete,
  MIN_REGISTRATION_AGE,
  validateRegistrationData,
} from "@/lib/auth";
import { BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import { capturePostPurchaseProfileCompletedPostHog } from "@/lib/posthogAnalytics";
import { RedirectToSignIn, useAuth, useUser } from "@clerk/clerk-react";
import { Clock, Loader2, Sparkles } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

const SEO = {
  title: "Completá tu perfil",
  description: `Completá tu perfil para acceder a tu membresía de ${BRAND_PERSON_NAME}.`,
  keywords: "",
  ogImage: `${BRAND_SITE_URL}/og.png`,
  canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO}`,
  robots: "noindex, nofollow" as const,
};

function toIsoDate(value: string): string {
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

function getMaxBirthDate(): string {
  const now = new Date();
  const max = new Date(now.getFullYear() - MIN_REGISTRATION_AGE, now.getMonth(), now.getDate());
  return max.toISOString().slice(0, 10);
}

function hasPaidPurchaseSignal(sp: URLSearchParams): boolean {
  const planTier = (sp.get("plan_tier") || "").trim().toLowerCase();
  if (sp.get("from_special_offer") === "1" || sp.get("from_vip") === "1") return true;
  if (sp.get("from_basic") === "1" || sp.get("from_standard") === "1") return true;
  if (sp.get("claimed_paid") === "1" || sp.get("from_recovery") === "1") return true;
  if (sp.get("session_id") || sp.get("subscription_id")) return true;
  if (["vip", "basic", "standard", "platinum", "devil"].includes(planTier)) return true;
  return false;
}

function buildPendingTarget(sessionId: string | null, sp: URLSearchParams): string {
  const q = new URLSearchParams();
  if (sessionId) q.set("session_id", sessionId);
  const mpSub = sp.get("subscription_id");
  if (mpSub) q.set("subscription_id", mpSub);
  const planTier = sp.get("plan_tier");
  if (planTier) q.set("plan_tier", planTier);
  if (sp.get("from_basic") === "1") q.set("from_basic", "1");
  if (sp.get("from_standard") === "1") q.set("from_standard", "1");
  if (sp.get("from_special_offer") === "1" || sp.get("from_vip") === "1") q.set("from_vip", "1");
  if (sp.get("access_request") === "1") q.set("access_request", "1");
  if (sp.get("claimed_paid") === "1" || sp.get("from_recovery") === "1") {
    q.set("claimed_paid", "1");
    q.set("from_recovery", "1");
  }
  // Sin compra: pending ofrece CTA a la membresía VIP canónica (Stripe)
  if (!hasPaidPurchaseSignal(sp) && sp.get("from_free") !== "true") {
    q.set("offer_membership", "1");
  }
  const qs = q.toString();
  return qs ? `${MEMBERSHIP_ROUTE.PENDING_APPROVAL}?${qs}` : MEMBERSHIP_ROUTE.PENDING_APPROVAL;
}

/** Seed Clerk publicMetadata (role/status/pending) so session JWT is not {}. */
async function seedPendingClerkMetadata(
  getToken: () => Promise<string | null>,
): Promise<void> {
  try {
    const token = await getToken();
    if (!token) return;
    await fetch("/api/sync-my-membership", {
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    });
  } catch {
    /* best-effort */
  }
}

export default function CompleteRegistration() {
  useSEO(SEO);
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [params] = useSearchParams();

  const [alias, setAlias] = useState("");
  const [telegram, setTelegram] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const maxBirthDate = useMemo(() => getMaxBirthDate(), []);

  const fromVip = params.get("from_special_offer") === "1" || params.get("from_vip") === "1";
  const planTier = (params.get("plan_tier") || "").trim().toLowerCase();
  const fromBasic = params.get("from_basic") === "1" || planTier === "basic";
  const fromStandard =
    params.get("from_standard") === "1" ||
    (planTier === "standard" && !fromBasic && !fromVip);
  const sessionId = params.get("session_id");
  const mercadopagoSubscriptionId = params.get("subscription_id");
  const pendingTarget = buildPendingTarget(sessionId, params);

  // Pre-fill desde unsafeMetadata si ya tiene algo
  useEffect(() => {
    if (!user) return;
    const data = getRegistrationData(user);
    if (!alias && data.alias) setAlias(data.alias);
    if (!telegram && data.telegram) setTelegram(data.telegram);
    if (!birthDate && data.birthDate) {
      const parsed = new Date(data.birthDate);
      if (!Number.isNaN(parsed.getTime())) {
        setBirthDate(parsed.toISOString().slice(0, 10));
      }
    }
  }, [alias, telegram, birthDate, user]);

  /**
   * Hook al pipeline de membresías:
   * En cuanto el user está signed-in en esta página, le mandamos al backend
   * un sync con action="linked" para que se setee el clerk_user_id en
   * user_memberships (idempotente — solo escribe si todavía no estaba).
   *
   * Sin este hook el stage 2 (`registered`) del modelo del admin no se
   * alcanza nunca (queda vacío) porque ningún otro código popula
   * clerk_user_id. Después del pago el visitante salta de stage 1 (paid)
   * directo a stage 4 (pending_approval). Con este hook, el operador puede
   * ver en `/admin/memberships` quién firmó pero no completó perfil.
   *
   * Idempotente + fire-and-forget: ignoramos errors (incluyen 401 si la
   * sesión expira justo aquí). El subsecuente call al save (action=
   * profile_complete) también va a setear clerk_user_id como fallback.
   */
  useEffect(() => {
    if (!isSignedIn || !user) return;
    let aborted = false;
    void (async () => {
      try {
        const token = await getToken();
        if (!token || aborted) return;
        await fetch("/api/membership/sync-stage", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: "linked" }),
          keepalive: true,
        });
        // Seed publicMetadata (role/status/pending) for production session JWT
        if (!aborted) await seedPendingClerkMetadata(getToken);
        if (!aborted) await user.reload();
      } catch {
        // Best-effort. El save subsecuente va a hacer otro intent.
      }
    })();
    return () => {
      aborted = true;
    };
  }, [isSignedIn, user, getToken]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  // Si ya tiene perfil completo, ir a pending-approval
  // Excepción: free tier con free_telegram_url — necesita pasar por handleSubmit
  // para que /api/free-join quede registrado en la DB (flujo sign-in en /registro).
  const earlyFreeUrl = params.get("free_telegram_url");
  const isFreeWithUrl = planTier === "free" && Boolean(earlyFreeUrl);
  if (user && isProfileComplete(user) && !isFreeWithUrl) {
    if (isClerkAdmin(user)) return <Navigate to="/admin" replace />;
    return <Navigate to={pendingTarget} replace />;
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;
    const trimmedAlias = alias.trim();
    const trimmedTelegram = telegram.trim();
    const validationError = validateRegistrationData({
      alias: trimmedAlias,
      birthDate,
      telegram: trimmedTelegram,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const existingMeta = (user.unsafeMetadata ?? {}) as Record<string, unknown>;
      await user.update({
        unsafeMetadata: {
          ...existingMeta,
          alias: trimmedAlias,
          birthDate: toIsoDate(birthDate),
          telegram: trimmedTelegram,
          ...(fromVip ? { from_vip_signup: true } : {}),
          ...(fromBasic ? { from_basic_signup: true } : {}),
          ...(fromStandard ? { from_standard_signup: true } : {}),
          ...(planTier ? { purchase_plan_tier: planTier } : {}),
          ...(sessionId ? { stripe_session_id: sessionId } : {}),
          ...(mercadopagoSubscriptionId
            ? { mercadopago_subscription_id: mercadopagoSubscriptionId }
            : {}),
        },
      });

      capturePostPurchaseProfileCompletedPostHog({
        plan_tier: planTier || null,
        from_vip: fromVip,
        from_basic: fromBasic,
        from_standard: fromStandard,
        stripe_session_id: sessionId,
        mercadopago_subscription_id: mercadopagoSubscriptionId,
      });

      // Hook al pipeline de membresías: marca profile_completed_at = NOW()
      // en user_memberships. Sin esto el stage 3 (`profile_complete`) del
      // modelo del admin queda vacío y el operador no puede medir latency
      // entre Clerk sign-up y completar perfil. Fire-and-forget: si falla
      // el navigate sigue, el endpoint es idempotente y el sync inicial
      // (action=linked) ya seteó clerk_user_id como fallback.
      try {
        const token = await getToken();
        if (token) {
          await fetch("/api/membership/sync-stage", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ action: "profile_complete" }),
            keepalive: true,
          });
        }
      } catch {
        // Best-effort.
      }

      // Ensure Clerk publicMetadata is pending (not {}) before landing on pending-approval
      await seedPendingClerkMetadata(() => getToken({ skipCache: true }));
      try {
        await user.reload();
      } catch {
        /* ignore */
      }

      if (isClerkAdmin(user)) {
        navigate("/admin", { replace: true });
        return;
      }

      // Free join: llamar a /api/free-join y redirigir a pending-approval con from_free=true
      const variantFreeUrl = params.get("free_telegram_url");
      if (planTier === "free" && variantFreeUrl) {
        try {
          const token = await getToken();
          if (token) {
            await fetch("/api/free-join", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({ free_telegram_url: variantFreeUrl }),
              keepalive: true,
            });
          }
        } catch {
          // Best-effort: la aprobación admin puede crear el registro si esta llamada falla
        }
        navigate(`${MEMBERSHIP_ROUTE.PENDING_APPROVAL}?from_free=true`, { replace: true });
        return;
      }

      // Tras completar perfil → pending (equipo asigna link de ingreso)
      navigate(pendingTarget, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar el perfil.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 py-8 flex items-center justify-center">
      <div className="mx-auto max-w-md w-full">
        <Card className="border-white/10 bg-zinc-900/80 backdrop-blur shadow-2xl shadow-black/40">
          <CardHeader className="text-center space-y-3 pb-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-amber-300" />
            </div>
            <CardTitle className="text-xl sm:text-2xl text-zinc-100">¡Último paso!</CardTitle>
            <CardDescription className="text-sm text-zinc-400">
              {fromBasic || fromStandard
                ? `Completá estos datos para que el equipo pueda validar tu acceso al grupo y enviarte el link correspondiente.`
                : `Completá tu perfil para que el equipo pueda asignarte el link privado de ingreso.`}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-2">
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-1.5">
                <Label htmlFor="alias" className="text-zinc-200">Alias o apodo</Label>
                <Input
                  id="alias"
                  value={alias}
                  onChange={(e) => {
                    setAlias(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="¿Cómo te gustaría que te llame?"
                  minLength={3}
                  maxLength={30}
                  required
                  className="bg-black/40 border-white/10 text-zinc-100 placeholder:text-zinc-500 h-11"
                />
                <p className="text-xs text-zinc-500">Entre 3 y 30 caracteres</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="telegram" className="text-zinc-200">Telegram</Label>
                <Input
                  id="telegram"
                  value={telegram}
                  onChange={(e) => {
                    setTelegram(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="@usuario o +54911..."
                  minLength={3}
                  maxLength={50}
                  required
                  className="bg-black/40 border-white/10 text-zinc-100 placeholder:text-zinc-500 h-11"
                />
                <p className="text-xs text-zinc-500">
                  Necesario para asignarte el grupo privado.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="birthDate" className="text-zinc-200">Fecha de nacimiento</Label>
                <Input
                  id="birthDate"
                  type="date"
                  value={birthDate}
                  max={maxBirthDate}
                  onChange={(e) => {
                    setBirthDate(e.target.value);
                    if (error) setError(null);
                  }}
                  required
                  className="bg-black/40 border-white/10 text-zinc-100 h-11"
                />
                <p className="text-xs text-zinc-500">+{MIN_REGISTRATION_AGE} años</p>
              </div>

              {error && (
                <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-xs text-rose-200">
                  {error}
                </div>
              )}

              {(fromVip || fromBasic || fromStandard) && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs text-amber-100 flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                  <span>
                    {fromVip
                      ? "Después de guardar, el equipo asignará tu link privado de ingreso (puede demorar unas horas según el horario)."
                      : "Después de guardar, el equipo validará tu cuenta y te enviará el link de ingreso cuando corresponda."}
                  </span>
                </div>
              )}

              <Button
                type="submit"
                disabled={saving}
                className="w-full h-11 bg-white text-black font-semibold hover:bg-white/90 gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Guardar y continuar"
                )}
              </Button>

              <MembershipPortalHint className="pt-1" />
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
